import { CommonModule } from '@angular/common';
import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule, ModalController } from '@ionic/angular';
import _ from 'lodash';

import { SlackMarkdown } from 'src/app/components/slack-blocks/slack-markdown';
import { WorkingHoursComponent } from 'src/app/components/working-hours/working-hours.component';
import { AuthService } from 'src/services/auth.service';

import { SlackTextEditorComponent } from '../slack-text-editor/slack-text-editor.component';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, SlackTextEditorComponent],
  selector: 'app-autoresponder',
  templateUrl: './autoresponder.component.html',
  styleUrls: ['./autoresponder.component.css'],
})
export class AutoresponderComponent implements OnInit {
  @Input() isDefault: boolean;
  @Input() initialAutoresponder: any;
  @Input() projectId?: string | null;
  @Output() autoresponderDidChange = new EventEmitter();

  overrideAutoresponder?: boolean;
  autoresponder?: any = {};
  autoresponderKnowledgeBaseEnabled: boolean;
  autoresponderKnowledgeBaseCondition: string;
  autoresponderAiResponsesEnabled: boolean;
  autoresponderAiResponsesCondition: string;
  aiResponsePrefixEnabled: boolean;
  aiResponseFallbackEnabled: boolean;
  slackMarkdown = new SlackMarkdown();

  constructor(
    public authService: AuthService,
    private modalCtrl: ModalController,
  ) {}

  ngOnInit() {
    this.overrideAutoresponder = this.isDefault || this.initialAutoresponder ? true : false;
    this.autoresponder =
      !this.isDefault && this.initialAutoresponder
        ? {
            ...this.initialAutoresponder,
            message: this.autoresponderContentToHtml(this.initialAutoresponder?.message ?? ''),
            alternateMessage: this.autoresponderContentToHtml(this.initialAutoresponder?.alternateMessage ?? ''),
            aiResponsePrefix: this.autoresponderContentToHtml(this.initialAutoresponder?.aiResponsePrefix ?? ''),
            aiResponseFallback: this.autoresponderContentToHtml(this.initialAutoresponder?.aiResponseFallback ?? ''),
            disclaimerMessage: this.initialAutoresponder?.disclaimerMessage ?? '',
          }
        : {
            ...this.authService.tenant.autoresponder,
            message: this.autoresponderContentToHtml(this.authService.tenant?.autoresponder?.message ?? ''),
            alternateMessage: this.autoresponderContentToHtml(
              this.authService.tenant?.autoresponder?.alternateMessage ?? '',
            ),
            aiResponsePrefix: this.autoresponderContentToHtml(
              this.authService.tenant?.autoresponder?.aiResponsePrefix ?? '',
            ),
            aiResponseFallback: this.autoresponderContentToHtml(
              this.authService.tenant?.autoresponder?.aiResponseFallback ?? '',
            ),
            disclaimerMessage: (this.authService.tenant?.autoresponder as any)?.disclaimerMessage ?? '',
          };
    this.autoresponderKnowledgeBaseEnabled =
      this.autoresponder?.includeKnowledgeBaseLinks && this.autoresponder?.includeKnowledgeBaseLinks !== 'off';
    this.autoresponderKnowledgeBaseCondition = this.autoresponderKnowledgeBaseEnabled
      ? this.autoresponder.includeKnowledgeBaseLinks
      : 'always';
    this.autoresponderAiResponsesEnabled =
      this.autoresponder?.includeAiResponse && this.autoresponder?.includeAiResponse !== 'off';
    this.autoresponderAiResponsesCondition = this.autoresponderAiResponsesEnabled
      ? this.autoresponder?.includeAiResponse
      : 'always';
    this.aiResponsePrefixEnabled = this.autoresponder?.aiResponsePrefix !== '';
    this.aiResponseFallbackEnabled = this.autoresponder?.aiResponseFallback !== '';
  }

  // Older autoresponders used to use Slack Markdown, but now we use HTML. Detect if the content is already in HTML and return it as is.
  autoresponderContentToHtml(content: string) {
    if (content.startsWith('<p>') && content.endsWith('</p>')) {
      return content;
    }

    return `<p>${this.slackMarkdown.toHtml(content)}</p>`;
  }

  onMessageContentChange(event) {
    this.autoresponder.message = event;
    this.handleChange();
  }

  onAlternateMessageContentChange(event) {
    this.autoresponder.alternateMessage = event;
    this.handleChange();
  }

  onPrefixContentChange(event) {
    this.autoresponder.aiResponsePrefix = event;
    this.handleChange();
  }

  onFallbackContentChange(event) {
    this.autoresponder.aiResponseFallback = event;
    this.handleChange();
  }

  onDisclaimerMessageContentChange(event) {
    this.autoresponder.disclaimerMessage = event;
    this.handleChange();
  }

  handleChange() {
    this.autoresponderDidChange.emit(
      this.overrideAutoresponder && this.autoresponder
        ? {
            ...this.autoresponder,
            message: this.autoresponder.message,
            alternateMessage: this.autoresponder.alternateMessage,
            aiResponsePrefix:
              this.aiResponsePrefixEnabled && this.autoresponder.aiResponsePrefix
                ? this.autoresponder.aiResponsePrefix
                : '',
            aiResponseFallback:
              this.aiResponseFallbackEnabled && this.autoresponder.aiResponseFallback
                ? this.autoresponder.aiResponseFallback
                : '',
            includeKnowledgeBaseLinks: this.autoresponderKnowledgeBaseEnabled
              ? this.autoresponderKnowledgeBaseCondition
              : 'off',
            includeAiResponse: this.autoresponderAiResponsesEnabled ? this.autoresponderAiResponsesCondition : 'off',
            disclaimerMessage: this.autoresponder.disclaimerMessage || '',
          }
        : null,
    );
  }

  async editWorkingHours() {
    const modal = await this.modalCtrl.create({
      component: WorkingHoursComponent,
      componentProps: {
        originalWorkingHours: this.authService.tenant?.workingHours ?? {},
        onSave: async (updates: any) => this.authService.updateTenant(updates),
      },
    });

    modal.present();
  }
}
